// safety/safety-walks/pages/VerifyCloseOutPage.tsx
// VerifyCloseOut screen (Figma): two-column close-out review for a Safety
// Officer. Left: original observation + submitted close-out (corrective
// action, resolution notes, after photos) + inline return-for-correction
// panel + activity timeline. Right: Your Decision, What happens on each
// action, Submission Details.
//   Verify & Close  -> Pending Verification -> Closed   (Email 07 to RP)
//   Return          -> Pending Verification -> In Progress (Email 08 + reason,
//                      reason is mandatory — backend enforces it too)
// Both actions end on a transient success screen, then navigate back to the
// walk's Observations tab.
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Loader2, CheckCircle2, RotateCcw, ImageOff, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useAbility } from '@casl/react';
import { AbilityContext } from '../../../../context/casl/AbilityContext';
import { ACTIONS, RESOURCES } from '../../../../constants/permissions.constants';
import { getObservation, verifyObservation, returnObservation } from '../../../../utils/safety-walks-api';
import { StatusPill, RiskBadge, fmtDate, fmtDateTime, closeOutSubmission, initials, PhotoThumb, PhotoLightbox } from '../components/shared';
import type { SafetyObservation, ActivityComment, PhotoMeta } from '../../../../types/safety-walk.types';

type DoneState = 'verified' | 'returned' | null;

const SUCCESS_REDIRECT_MS = 2500;

export const VerifyCloseOutPage: React.FC = () => {
    const { obsId } = useParams<{ obsId: string }>();
    const id = Number(obsId);
    const navigate = useNavigate();
    const ability = useAbility(AbilityContext);

    const [obs, setObs] = useState<SafetyObservation | null>(null);
    const [loading, setLoading] = useState(true);
    const [acting, setActing] = useState(false);
    const [done, setDone] = useState<DoneState>(null);
    // Lightbox preview for before/after photos (full-resolution view)
    const [previewPhoto, setPreviewPhoto] = useState<PhotoMeta | null>(null);

    // Inline return-for-correction panel
    const [returnOpen, setReturnOpen] = useState(false);
    const [returnReason, setReturnReason] = useState('');
    const returnPanelRef = useRef<HTMLDivElement>(null);

    const canVerify = ability.can(ACTIONS.VERIFY, RESOURCES.SAFETY_OBSERVATION);
    const canReturn = ability.can(ACTIONS.RETURN, RESOURCES.SAFETY_OBSERVATION);

    useEffect(() => {
        if (!id) return;
        setLoading(true);
        getObservation(id)
            .then((res) => setObs(res.observation || null))
            .catch(() => { /* api util toasts */ })
            .finally(() => setLoading(false));
    }, [id]);

    const backTarget = obs?.walk ? `/safety/walks/${obs.walk.id}?tab=observations` : '/safety/observations';

    // Success screens auto-return to the walk's Observations tab
    useEffect(() => {
        if (!done) return;
        const timer = setTimeout(() => navigate(backTarget), SUCCESS_REDIRECT_MS);
        return () => clearTimeout(timer);
    }, [done, backTarget, navigate]);

    const openReturnPanel = () => {
        setReturnOpen(true);
        // Panel renders below; scroll it into view on the next paint
        setTimeout(() => returnPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    };

    const handleVerify = async () => {
        if (!obs || acting) return;
        setActing(true);
        try {
            const res = await verifyObservation(id);
            setObs(res.observation);
            setDone('verified');
        } catch (error: any) {
            toast.error(error.message || 'Verification failed');
        } finally {
            setActing(false);
        }
    };

    const handleReturn = async () => {
        if (!obs || acting || !returnReason.trim()) return;
        setActing(true);
        try {
            const res = await returnObservation(id, returnReason.trim());
            setObs(res.observation);
            setDone('returned');
        } catch (error: any) {
            toast.error(error.message || 'Failed to return close-out');
        } finally {
            setActing(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-24">
                <Loader2 size={24} className="animate-spin text-muted-foreground" />
            </div>
        );
    }

    if (!obs) {
        return (
            <div className="max-w-[1200px] mx-auto px-4 py-16 text-center text-muted-foreground">
                Observation not found.
            </div>
        );
    }

    /* ---------------- Success screens (transient) ---------------- */
    if (done) {
        const verified = done === 'verified';
        return (
            <div className="pb-10 px-0 sm:px-4 lg:px-0 max-w-[1400px] mx-auto w-full">
                <div className="max-w-2xl mx-auto mt-16 rounded-xl border border-border bg-card p-10 text-center">
                    <div className={`mx-auto h-14 w-14 rounded-full flex items-center justify-center mb-4 ${verified ? 'bg-emerald-50' : 'bg-amber-50'}`}>
                        {verified ? <CheckCircle2 size={28} className="text-emerald-600" /> : <RotateCcw size={28} className="text-amber-600" />}
                    </div>
                    <h1 className="text-xl font-semibold">{verified ? 'Observation Verified & Closed' : 'Close-Out Returned'}</h1>
                    <p className="text-sm text-muted-foreground mt-3">
                        {verified ? (
                            <>
                                {obs.obsNumber} – {obs.title || 'Untitled observation'} has been verified. Status is now{' '}
                                <strong className="text-foreground">Closed</strong>. {obs.personResponsibleName || 'The responsible person'} has been notified.
                            </>
                        ) : (
                            <>
                                {obs.obsNumber} has been returned to {obs.personResponsibleName || 'the responsible person'} with your comments.
                                Status is back to <strong className="text-foreground">In Progress</strong>.
                            </>
                        )}
                    </p>
                    <button
                        onClick={() => navigate(backTarget)}
                        className="mt-6 inline-flex items-center gap-2 h-10 px-5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90"
                    >
                        <ChevronLeft size={15} /> Back to Observations
                    </button>
                </div>
            </div>
        );
    }

    const isPending = obs.status === 'Pending Verification';
    const submission = closeOutSubmission(obs);
    const recordedBy = (obs.audit || []).find((a) => a.action === 'Observation Recorded')?.who;
    const comments: ActivityComment[] = Array.isArray(obs.comments) ? obs.comments : [];
    const beforePhotos = Array.isArray(obs.beforePhoto) ? obs.beforePhoto : [];
    const afterPhotos = Array.isArray(obs.afterPhoto) ? obs.afterPhoto : [];

    if (!isPending) {
        return (
            <div className="pb-10 px-0 sm:px-4 lg:px-0 max-w-[1400px] mx-auto w-full">
                <div className="max-w-2xl mx-auto mt-16 rounded-xl border border-border bg-card p-10 text-center">
                    <h1 className="text-xl font-semibold">{obs.obsNumber} is not awaiting verification</h1>
                    <p className="text-sm text-muted-foreground mt-3">
                        Current status: <StatusPill status={obs.status} />
                    </p>
                    <button
                        onClick={() => navigate(`/safety/observations/${obs.id}`)}
                        className="mt-6 inline-flex items-center gap-2 h-10 px-5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90"
                    >
                        View Observation
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="pb-10 px-0 sm:px-4 lg:px-0 max-w-[1400px] mx-auto w-full">
            {/* Header */}
            <div className="flex items-start justify-between mb-5 gap-3 flex-wrap">
                <div>
                    <button onClick={() => navigate(backTarget)} className="text-sm text-primary hover:underline mb-1 inline-flex items-center gap-1">
                        <ChevronLeft size={14} /> {obs.walk ? `${obs.walk.walkNumber} — Observations` : 'My Safety Observations'}
                    </button>
                    <div className="flex items-center gap-2 flex-wrap">
                        <h1 className="text-lg font-semibold">{obs.obsNumber}</h1>
                        <RiskBadge risk={obs.riskRating} />
                        {obs.category && <span className="text-sm text-muted-foreground">{obs.category}</span>}
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border bg-violet-50 text-violet-700 border-violet-200">
                            <span className="relative flex h-1.5 w-1.5">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-violet-400 opacity-75" />
                                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-violet-500" />
                            </span>
                            Pending Verification
                        </span>
                    </div>
                    <p className="text-2xl font-medium mt-1">{obs.title || 'Untitled observation'}</p>
                    <p className="text-sm text-muted-foreground mt-1">
                        Submitted by <strong className="text-foreground">{submission.who}</strong>
                        {submission.when ? ` on ${fmtDateTime(submission.when)}` : ''}
                    </p>
                </div>
                {(canVerify || canReturn) && (
                    <div className="flex items-center gap-2 flex-wrap">
                        {canReturn && (
                            <button
                                onClick={openReturnPanel}
                                className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-amber-300 bg-amber-50 text-amber-700 text-sm font-medium hover:bg-amber-100"
                            >
                                <RotateCcw size={16} /> Return
                            </button>
                        )}
                        {canVerify && (
                            <button
                                onClick={handleVerify}
                                disabled={acting}
                                className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
                            >
                                {acting ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />} Verify &amp; Close
                            </button>
                        )}
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left: original observation + submitted close-out + return panel + timeline */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Original observation */}
                    <div className="rounded-xl border border-border bg-card p-5">
                        <h3 className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold mb-3">Original Observation</h3>
                        <p className="text-sm text-foreground whitespace-pre-wrap">{obs.description || 'No description provided.'}</p>
                        <div className="mt-4">
                            {beforePhotos.length > 0 ? (
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                    {beforePhotos.map((photo) => (
                                        <PhotoThumb key={photo.id} photo={photo} onView={setPreviewPhoto} />
                                    ))}
                                </div>
                            ) : (
                                <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border py-10 text-muted-foreground">
                                    <ImageOff size={28} className="mb-2" />
                                    <span className="text-xs font-medium">Before Photo</span>
                                    <span className="text-xs">No photo uploaded</span>
                                </div>
                            )}
                        </div>
                        <div className="mt-4 pt-4 border-t border-border flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                            <span>Recorded: <strong className="text-foreground">{fmtDate(obs.createdAt)}</strong></span>
                            {recordedBy && <span>By: <strong className="text-foreground">{recordedBy}</strong></span>}
                            <span>Due: <strong className="text-foreground">{fmtDate(obs.dueDate)}</strong></span>
                        </div>
                    </div>

                    {/* Submitted close-out */}
                    <div className="rounded-xl border border-indigo-200 bg-card p-5">
                        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
                            <div className="flex items-center gap-2.5">
                                <span className="h-8 w-8 rounded-full bg-indigo-100 text-indigo-700 text-xs font-semibold flex items-center justify-center shrink-0">
                                    {initials(submission.who)}
                                </span>
                                <h3 className="text-sm font-semibold">Close-Out Submitted by {submission.who}</h3>
                            </div>
                            {submission.when && <span className="text-xs text-muted-foreground">{fmtDateTime(submission.when)}</span>}
                        </div>

                        <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold mb-1.5">Corrective Action Taken</p>
                        <div className="rounded-lg border border-border bg-muted/30 px-3 py-2.5">
                            <p className="text-sm whitespace-pre-wrap">{obs.correctiveAction || '—'}</p>
                        </div>

                        {obs.resolutionNotes && (
                            <>
                                <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold mt-4 mb-1.5">Resolution Notes</p>
                                <div className="rounded-lg border border-border bg-muted/30 px-3 py-2.5">
                                    <p className="text-sm whitespace-pre-wrap">{obs.resolutionNotes}</p>
                                </div>
                            </>
                        )}

                        <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold mt-4 mb-1.5">After Photo</p>
                        {afterPhotos.length > 0 ? (
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                {afterPhotos.map((photo) => (
                                    <PhotoThumb key={photo.id} photo={photo} onView={setPreviewPhoto} />
                                ))}
                            </div>
                        ) : (
                            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border py-10 text-muted-foreground">
                                <ImageOff size={28} className="mb-2" />
                                <span className="text-xs">No after photo uploaded</span>
                            </div>
                        )}

                        <div className="mt-4 pt-4 border-t border-border flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
                            <span>Date Fixed: <strong className="text-foreground">{fmtDate(obs.fixedDate)}</strong></span>
                            <span>Submitted By: <strong className="text-foreground">{submission.who}</strong></span>
                        </div>
                    </div>

                    {/* Inline return-for-correction panel */}
                    {returnOpen && canReturn && (
                        <div ref={returnPanelRef} className="rounded-xl border border-amber-300 bg-amber-50/40 p-5 scroll-mt-4">
                            <div className="flex items-center gap-2 mb-3">
                                <RotateCcw size={16} className="text-amber-600" />
                                <h3 className="text-sm font-semibold">Return Close-Out for Correction</h3>
                            </div>
                            <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 mb-4">
                                <AlertTriangle size={15} className="text-amber-500 shrink-0 mt-0.5" />
                                <p className="text-xs text-amber-800">
                                    The observation will move back to <strong>In Progress</strong>. Your comments will be sent to{' '}
                                    {obs.personResponsibleName || 'the responsible person'} via email and shown in the activity timeline.
                                </p>
                            </div>
                            <label className="text-xs font-medium text-muted-foreground mb-1.5 block">REASON FOR RETURN *</label>
                            <textarea
                                value={returnReason}
                                onChange={(e) => setReturnReason(e.target.value)}
                                placeholder='e.g. "The after photo does not clearly show the repair. Please upload a clearer image showing the full fix."'
                                rows={3}
                                className="w-full px-3 py-2 rounded-lg border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-amber-300 resize-none"
                            />
                            <div className="flex items-center gap-3 mt-4">
                                <button
                                    onClick={handleReturn}
                                    disabled={acting || !returnReason.trim()}
                                    className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-amber-500 text-white text-sm font-medium hover:bg-amber-600 disabled:opacity-50"
                                >
                                    {acting ? <Loader2 size={15} className="animate-spin" /> : <RotateCcw size={15} />}
                                    Confirm Return
                                </button>
                                <button
                                    onClick={() => { setReturnOpen(false); setReturnReason(''); }}
                                    disabled={acting}
                                    className="h-10 px-4 rounded-lg border border-border bg-card text-sm font-medium hover:bg-accent"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Activity timeline (read-only context for the review) */}
                    <div className="rounded-xl border border-border bg-card p-5">
                        <h3 className="text-sm font-semibold mb-4">Activity Timeline</h3>
                        <div className="space-y-4">
                            {comments.map((c) => (
                                <div key={c.id} className="flex gap-3">
                                    <span className="h-7 w-7 rounded-full bg-muted text-muted-foreground text-[10px] font-medium flex items-center justify-center shrink-0 mt-0.5">
                                        {initials(c.authorName)}
                                    </span>
                                    <div>
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-sm font-medium">{c.authorName}</span>
                                            <span className="text-xs text-muted-foreground">{fmtDateTime(c.date)}</span>
                                            {c.type === 'system' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">system</span>}
                                        </div>
                                        <p className="text-sm text-muted-foreground mt-0.5 whitespace-pre-wrap">{c.text}</p>
                                    </div>
                                </div>
                            ))}
                            {comments.length === 0 && (
                                <p className="text-sm text-muted-foreground">No activity yet</p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Right: decision + explainer + submission details */}
                <div className="space-y-6">
                    {(canVerify || canReturn) && (
                        <div className="rounded-xl border border-border bg-card p-5">
                            <h3 className="text-sm font-semibold mb-1">Your Decision</h3>
                            <p className="text-xs text-muted-foreground mb-4">
                                Review the corrective action, resolution notes, and after photo, then verify or return.
                            </p>
                            <div className="space-y-2">
                                {canVerify && (
                                    <button
                                        onClick={handleVerify}
                                        disabled={acting}
                                        className="w-full inline-flex items-center justify-center gap-2 h-11 px-4 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
                                    >
                                        {acting ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                                        Verify &amp; Close Observation
                                    </button>
                                )}
                                {canReturn && (
                                    <button
                                        onClick={openReturnPanel}
                                        disabled={acting}
                                        className="w-full inline-flex items-center justify-center gap-2 h-11 px-4 rounded-lg border border-border bg-card text-sm font-medium hover:bg-accent disabled:opacity-50"
                                    >
                                        <RotateCcw size={16} /> Return for Correction
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="rounded-xl border border-border bg-card p-5">
                        <h3 className="text-sm font-semibold mb-4">What happens on each action</h3>
                        <div className="space-y-4">
                            <div className="flex gap-2.5">
                                <CheckCircle2 size={16} className="text-emerald-600 shrink-0 mt-0.5" />
                                <div>
                                    <p className="text-sm font-medium text-emerald-700">Verify &amp; Close</p>
                                    <p className="text-xs text-muted-foreground mt-0.5">
                                        Status → Closed. Email 07 sent to {obs.personResponsibleName || 'the responsible person'}. Walk progress updated.
                                    </p>
                                </div>
                            </div>
                            <div className="flex gap-2.5">
                                <RotateCcw size={16} className="text-amber-600 shrink-0 mt-0.5" />
                                <div>
                                    <p className="text-sm font-medium text-amber-700">Return for Correction</p>
                                    <p className="text-xs text-muted-foreground mt-0.5">
                                        Status → In Progress. Email 08 + your reason sent to {obs.personResponsibleName || 'the responsible person'}. Comment added to timeline.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="rounded-xl border border-border bg-card p-5">
                        <h3 className="text-sm font-semibold mb-4">Submission Details</h3>
                        <div className="space-y-3 text-sm">
                            <div>
                                <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-0.5">Submitted By</div>
                                <div className="font-medium">{submission.who}</div>
                            </div>
                            <div>
                                <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-0.5">Submitted At</div>
                                <div className="font-medium">{submission.when ? fmtDateTime(submission.when) : '—'}</div>
                            </div>
                            <div>
                                <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-0.5">Date Fixed</div>
                                <div className="font-medium">{fmtDate(obs.fixedDate)}</div>
                            </div>
                            <div>
                                <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-0.5">Company</div>
                                <div className="font-medium">{obs.company || '—'}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Photo lightbox — full-resolution view */}
            <PhotoLightbox photo={previewPhoto} onClose={() => setPreviewPhoto(null)} />
        </div>
    );
};
